import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReportTemplate from '../../../components/reportPage/ReportTemplate';
import { fromInternalVehicle } from '../../../components/reportPage/adapters/internal';
import { fromDealerJob } from '../../../components/reportPage/adapters/dealer';
import { combineReports } from '../../../components/reportPage/adapters/complete';
import { jobLoader, reportFileUrl, useDealerJob } from '../../../components/reportPage/useDealerJob';
import { useReportService } from '../../../services/reportService';
import { isReportUnavailable } from '../../../components/reportPage/reportAccess';
import { useOwnReport } from '../../../components/reportPage/useOwnReport';
import { routesPath } from '../../../common/constants/routesPath';
import sampleReportResponse from '../sampleReportData.json';
import sampleInternalData from '../sampleCombinedReportData.json';
import {
    translationNotice,
    translationStatus,
    useRetry,
    withTranslationNotice,
} from '../../../components/reportPage/translationNotice';

const SAMPLE_JOB = {
    ...sampleReportResponse,
    reportState: 'ready',
    report: { ...sampleReportResponse.report, vinCode: sampleInternalData.vin },
};

/**
 * Complete report (2 credits): our inspection and the dealer's service documents on one page. The two requests
 * run side by side; the dealer part shows its own state (waiting, original PDF...) until its report is read.
 */
const CombinedVehicleReport = ({ sample, shared }) => {
    const { t, i18n } = useTranslation('reportPage');
    const { vin, id: jobUuid, sharedLink } = useParams();
    const { getInternalReport, getInternalReportBySharedLink, getInternalOverview, getReport, getReportBySharedLink } =
        useReportService();
    // `lang`: the page language the inspection on screen was asked in. After a language switch the old one stays on
    // screen until the new one arrives, and Download PDF waits for it (SC-QA-119 staging check)
    const [internal, setInternal] = useState({ vehicle: null, loading: true, denied: false, lang: null });
    // the AI overview of our internal data: its text, its translation state, and whether its request has finished
    // (SC-QA-74)
    const [overview, setOverview] = useState({ text: null, translation: null, done: false });
    const lang = i18n.resolvedLanguage;
    const [attempt, retry] = useRetry();
    const fetchInternal = () =>
        shared ? getInternalReportBySharedLink(sharedLink, lang) : getInternalReport(vin, lang);

    useEffect(() => {
        let cancelled = false;
        if (sample) {
            setInternal({ vehicle: sampleInternalData, loading: false, denied: false, lang });
            return undefined;
        }

        fetchInternal()
            .then(vehicle => !cancelled && setInternal({ vehicle, loading: false, denied: false, lang }))
            .catch(
                error =>
                    !cancelled &&
                    setInternal({ vehicle: null, loading: false, denied: isReportUnavailable(error), lang })
            );

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vin, sharedLink, sample, shared, lang]);

    const load = jobLoader({
        sample: sample && SAMPLE_JOB,
        sharedLink: shared && sharedLink,
        jobUuid,
        getReport,
        getReportBySharedLink,
        lang,
    });
    const dealer = useDealerJob(load, [sample, shared, sharedLink, jobUuid, lang], attempt);

    // until the dealer report is read (or when the dealer refused the order) the AI overview comes from our internal
    // data. A shared link only exists once the report is read, so only the owner's own page asks
    const needsInternalOverview =
        !sample && !shared && !!dealer.job && !(dealer.job.report && dealer.job.report.summaryData);
    useEffect(() => {
        if (!needsInternalOverview) return undefined;
        let cancelled = false;
        setOverview({ text: null, translation: null, done: false });
        getInternalOverview(jobUuid, lang).then(
            ({ text, translation }) => !cancelled && setOverview({ text, translation, done: true })
        );
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needsInternalOverview, jobUuid, lang]);

    // Try again on the translation notice asks every part again quietly: the page and the reader's place stay as
    // they are until each new answer comes (the dealer part through useDealerJob's refresh)
    useEffect(() => {
        if (!attempt || sample) return undefined;
        let cancelled = false;
        fetchInternal()
            .then(vehicle => !cancelled && vehicle && setInternal(current => ({ ...current, vehicle })))
            .catch(() => undefined);
        if (needsInternalOverview) {
            getInternalOverview(jobUuid, lang).then(
                ({ text, translation }) => !cancelled && text && setOverview({ text, translation, done: true })
            );
        }
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attempt]);
    // asked for and not back yet: Download PDF waits for it, so the PDF never misses the overview
    const overviewPending = needsInternalOverview && !overview.done;
    // the inspection on screen is still the previous language's: Download PDF waits for the new one
    const inspectionPending = internal.lang !== lang;
    // asked for and none came back: the AI section says so instead of disappearing
    const overviewMissing = needsInternalOverview && overview.done && !overview.text;

    // the job is not the user's order (or, without a job, neither is the inspection): show none of it
    const denied =
        !sample && !shared && (isReportUnavailable(dealer.error) || (!dealer.job && !jobUuid && internal.denied));
    const own = useOwnReport(vin, denied);

    const report = useMemo(() => {
        const inspection = fromInternalVehicle(internal.vehicle, t);
        const dealerPart = dealer.job
            ? fromDealerJob(dealer.job, t, {
                  originalUrl: sample ? '' : reportFileUrl(dealer.job),
                  supportHref: routesPath.PUBLIC.SUPPORT,
              })
            : null;
        const translated = combineReports(inspection, dealerPart, internal.vehicle, t, overview.text, overviewMissing);
        // one notice for the whole page, from our inspection, the service history and the AI overview (SC-QA-119)
        const status = translationStatus(
            internal.vehicle && internal.vehicle.translation,
            dealer.job && dealer.job.translation,
            // only while the page shows the internal overview: once the service history is read, its own summary
            // takes over and an old state of the internal one must not keep the notice up
            needsInternalOverview ? overview.translation : null
        );
        const notice = translationNotice(status, t, retry);

        // the job request failed: the inspection stands alone, with a note that the dealer part could not be loaded
        if (translated && !dealerPart && dealer.error) {
            return {
                ...translated,
                notices: [
                    notice,
                    {
                        tone: 'warn',
                        title: t('complete.dealerLoadErrorTitle'),
                        text: t('complete.dealerLoadErrorText'),
                        screenOnlyText: true,
                    },
                ].filter(Boolean),
            };
        }
        return withTranslationNotice(translated, notice);
    }, [
        internal.vehicle,
        dealer.job,
        dealer.error,
        t,
        sample,
        overview.text,
        overview.translation,
        overviewMissing,
        needsInternalOverview,
        retry,
    ]);

    return (
        <ReportTemplate
            report={report}
            loading={internal.loading || dealer.loading || own.loading}
            downloadPending={overviewPending || inspectionPending}
            unavailable={denied ? { ownRecord: own.record } : null}
            showBack={!shared && !sample}
            sharedLink={!shared && !sample && dealer.job && dealer.job.report ? dealer.job.report.sharedLink : null}
            printTitle={
                internal.vehicle ? `${String(internal.vehicle.vin || '').toUpperCase()} - CheckVin.ai` : undefined
            }
        />
    );
};

export default CombinedVehicleReport;
